import React from 'react';
import './BolusEntryCard.css';

interface BolusEntry {
  id: string;
  timestamp: string | number;
  insulin_units: number;
  carbs_consumed?: number;
  blood_glucose_before?: number;
  meal_type?: string;
  injection_site?: string;
  food_description?: string;
}

interface BolusEntryCardProps {
  entry: BolusEntry;
  onEdit?: (entry: BolusEntry) => void;
  onDelete?: (id: string) => void;
}

const BolusEntryCard: React.FC<BolusEntryCardProps> = ({ entry, onEdit, onDelete }) => {
  const date = new Date(typeof entry.timestamp === 'number' ? entry.timestamp * 1000 : entry.timestamp);
  const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateString = date.toLocaleDateString([], { month: 'short', day: 'numeric' });

  const getMealIcon = (mealType?: string) => {
    switch (mealType?.toLowerCase()) {
      case 'breakfast': return '🎯';
      case 'lunch': return '🍔';
      case 'dinner': return '🍽️';
      case 'snack': return '🍏';
      default: return '💉';
    }
  };

  return (
    <div className="bolus-entry-card">
      <div className="card-header">
        <span className="entry-time">{timeString}</span>
        <span className="entry-date">{dateString}</span>
        <div className="card-actions">
          {onEdit && <button onClick={() => onEdit(entry)} className="btn-icon">✏️</button>}
          {onDelete && <button onClick={() => onDelete(entry.id)} className="btn-icon btn-delete">🗑️</button>}
        </div>
      </div>
      
      <div className="card-body">
        <div className="main-stat">
          <span className="stat-value">{entry.insulin_units.toFixed(1)}</span>
          <span className="stat-unit">units</span>
          <span className="stat-label">Insulin</span>
        </div>

        <div className="secondary-stats">
          {entry.carbs_consumed !== undefined && (
            <div className="stat-item">
              <span className="item-icon">🍞</span>
              <span className="item-value">{entry.carbs_consumed}g</span>
              <span className="item-label">Carbs</span>
            </div>
          )}
          {entry.blood_glucose_before !== undefined && (
            <div className="stat-item">
              <span className="item-icon">🩸</span>
              <span className="item-value">{entry.blood_glucose_before}</span>
              <span className="item-label">BG Before</span>
            </div>
          )}
        </div>
      </div>

      {(entry.meal_type || entry.injection_site || entry.food_description) && (
        <div className="card-footer">
          <div className="metadata">
            {entry.meal_type && (
              <span className="badge meal-badge">
                {getMealIcon(entry.meal_type)} {entry.meal_type}
              </span>
            )}
            {entry.injection_site && (
              <span className="badge site-badge">
                📍 {entry.injection_site}
              </span>
            )}
          </div>
          {entry.food_description && (
            <p className="entry-notes">{entry.food_description}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default BolusEntryCard;
